import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { User, Lock, Save, Eye, EyeOff } from 'lucide-react';

export const MiCuenta = () => {
  const { API, token, usuario, login } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [perfilData, setPerfilData] = useState({
    nombre: '',
    email: '',
    especialidad: '',
    nif: ''
  });
  const [passwordData, setPasswordData] = useState({
    password_actual: '',
    password_nueva: '',
    password_nueva_confirmar: ''
  });
  const [showPassword, setShowPassword] = useState({
    actual: false,
    nueva: false,
    confirmar: false
  });

  useEffect(() => {
    if (usuario) {
      setPerfilData({
        nombre: usuario.nombre || '',
        email: usuario.email || '',
        especialidad: usuario.especialidad || '',
        nif: usuario.nif || ''
      });
    }
  }, [usuario]);

  const guardarPerfil = async () => {
    try {
      setLoading(true);
      const response = await axios.put(
        `${API}/auth/perfil`,
        perfilData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Actualizar el contexto con los nuevos datos
      login(token, response.data);
      
      toast.success('Perfil actualizado exitosamente');
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const cambiarPassword = async () => {
    if (passwordData.password_nueva !== passwordData.password_nueva_confirmar) {
      toast.error('Las contraseñas nuevas no coinciden');
      return;
    }

    if (passwordData.password_nueva.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${API}/auth/cambiar-password`,
        {
          password_actual: passwordData.password_actual,
          password_nueva: passwordData.password_nueva
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Contraseña actualizada exitosamente');
      
      // Limpiar formulario
      setPasswordData({
        password_actual: '',
        password_nueva: '',
        password_nueva_confirmar: ''
      });
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title flex items-center gap-3">
            <User size={28} className="text-purple-600" />
            Mi Cuenta
          </h1>
          <p className="page-subtitle">Gestiona tu información personal y contraseña</p>
        </div>

        <div className="page-body">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Perfil */}
            <Card className="card">
              <div className="flex items-center gap-3 mb-6">
                <User size={24} className="text-purple-600" />
                <h2 className="text-xl font-bold">Información Personal</h2>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre</Label>
                  <Input
                    id="nombre"
                    value={perfilData.nombre}
                    onChange={(e) => setPerfilData({ ...perfilData, nombre: e.target.value })}
                    placeholder="Tu nombre completo"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={perfilData.email}
                    onChange={(e) => setPerfilData({ ...perfilData, email: e.target.value })}
                    placeholder="tu@email.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="especialidad">Especialidad</Label>
                  <Input
                    id="especialidad"
                    value={perfilData.especialidad}
                    onChange={(e) => setPerfilData({ ...perfilData, especialidad: e.target.value })}
                    placeholder="Tu especialidad o área de trabajo"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="nif">NIF</Label>
                  <Input
                    id="nif"
                    value={perfilData.nif}
                    onChange={(e) => setPerfilData({ ...perfilData, nif: e.target.value })}
                    placeholder="Tu NIF o CIF"
                  />
                  <p className="text-xs text-gray-500">
                    Necesario para procesar facturas correctamente
                  </p>
                </div>
                
                <div className="pt-4">
                  <Button 
                    onClick={guardarPerfil} 
                    disabled={loading}
                    className="w-full"
                  >
                    <Save size={18} className="mr-2" />
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Cambiar Contraseña */}
            <Card className="card">
              <div className="flex items-center gap-3 mb-6">
                <Lock size={24} className="text-purple-600" />
                <h2 className="text-xl font-bold">Cambiar Contraseña</h2>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password_actual">Contraseña Actual</Label>
                  <div className="relative">
                    <Input
                      id="password_actual"
                      type={showPassword.actual ? "text" : "password"}
                      value={passwordData.password_actual}
                      onChange={(e) => setPasswordData({ ...passwordData, password_actual: e.target.value })}
                      placeholder="Ingresa tu contraseña actual"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword({ ...showPassword, actual: !showPassword.actual })}
                    >
                      {showPassword.actual ? <EyeOff size={18} /> : <Eye size={18} />}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password_nueva">Nueva Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="password_nueva"
                      type={showPassword.nueva ? "text" : "password"}
                      value={passwordData.password_nueva}
                      onChange={(e) => setPasswordData({ ...passwordData, password_nueva: e.target.value })}
                      placeholder="Mínimo 6 caracteres"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword({ ...showPassword, nueva: !showPassword.nueva })}
                    >
                      {showPassword.nueva ? <EyeOff size={18} /> : <Eye size={18} />}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password_confirmar">Confirmar Nueva Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="password_confirmar"
                      type={showPassword.confirmar ? "text" : "password"}
                      value={passwordData.password_nueva_confirmar}
                      onChange={(e) => setPasswordData({ ...passwordData, password_nueva_confirmar: e.target.value })}
                      placeholder="Repite la nueva contraseña"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword({ ...showPassword, confirmar: !showPassword.confirmar })}
                    >
                      {showPassword.confirmar ? <EyeOff size={18} /> : <Eye size={18} />}
                    </Button>
                  </div>
                </div>
                
                <div className="pt-4">
                  <Button 
                    onClick={cambiarPassword} 
                    disabled={loading || !passwordData.password_actual || !passwordData.password_nueva}
                    className="w-full"
                    variant="outline"
                  >
                    <Lock size={18} className="mr-2" />
                    {loading ? 'Cambiando...' : 'Cambiar Contraseña'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Información adicional */}
          <Card className="card mt-6">
            <h3 className="text-lg font-semibold mb-4">Información de la Cuenta</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-500">Rol</Label>
                <p className="font-medium">
                  {usuario?.rol === 'admin' ? 'Administrador' : 'Usuario'}
                </p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Estado</Label>
                <p className="font-medium">
                  {usuario?.activo ? (
                    <span className="text-green-600">Activo</span>
                  ) : (
                    <span className="text-red-600">Inactivo</span>
                  )}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Nota: El rol y el estado de tu cuenta solo pueden ser modificados por un administrador.
            </p>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default MiCuenta;

